// Redacts known-secret values + generic token patterns from a text stream,
// tolerating a secret split across two chunks.
//
// Strategy: keep a tail buffer of unflushed text across `feed()` calls. Each
// call re-scans (held-tail + new chunk), redacts everything it can safely
// confirm, then holds back a tail long enough to catch a secret that might
// continue into the NEXT chunk. `flush()` emits + redacts whatever tail
// remains (end of stream, nothing more can arrive to complete a split match).
//
// Hold-back length (re-review 2026-09-26, C1-F1): a FIXED 128 chars leaked
// the first (len - 128) chars of any longer secret — a real JWT is ~270+,
// measured intact at the sink. Two bounds now, the larger wins:
//   - a base of BASE_HOLD_BACK_CHARS, raised to 3× the longest known secret
//     value (its base64 / URL-encoded variants are up to 3× longer);
//   - the maximal token-like run (`[A-Za-z0-9._-]`, plus a "Bearer " prefix)
//     touching the END of the buffer — every generic pattern is made of those
//     chars, so a split token always ends the buffer with such a run. Bounded
//     so one giant base64 blob cannot pin the whole stream in memory.
const BASE_HOLD_BACK_CHARS = 128;
const KNOWN_SECRET_HOLD_BACK_FACTOR = 3;
const MAX_TOKEN_RUN_HOLD_BACK_CHARS = 65_536;
const BEARER_PREFIX_BEFORE_RUN = /Bearer\s+$/;

function isTokenChar(code: number): boolean {
  return (
    (code >= 48 && code <= 57) || // 0-9
    (code >= 65 && code <= 90) || // A-Z
    (code >= 97 && code <= 122) || // a-z
    code === 46 || // .
    code === 95 || // _
    code === 45 // -
  );
}

/** Length of the token-like run ending the text (+ its "Bearer " prefix), 0 when the text ends on a separator. */
function trailingTokenRunLength(text: string): number {
  let start = text.length;
  while (start > 0 && text.length - start < MAX_TOKEN_RUN_HOLD_BACK_CHARS) {
    if (!isTokenChar(text.charCodeAt(start - 1))) break;
    start--;
  }
  const run = text.length - start;
  if (run === 0) return 0;
  const prefix = BEARER_PREFIX_BEFORE_RUN.exec(text.slice(Math.max(0, start - 12), start));
  return run + (prefix ? prefix[0].length : 0);
}

const GENERIC_TOKEN_PATTERNS: RegExp[] = [
  /sk-ant-[A-Za-z0-9_-]{10,}/g,
  /ghp_[A-Za-z0-9]{10,}/g,
  /glpat-[A-Za-z0-9_-]{10,}/g,
  /AKIA[A-Z0-9]{12,}/g,
  /eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}/g, // JWT-like
  /Bearer\s+[A-Za-z0-9._-]{8,}/g,
];

const REDACTED_PLACEHOLDER = "[REDACTED]";

function escapeRegExp(literal: string): string {
  return literal.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function buildKnownSecretPatterns(secretValues: string[]): RegExp[] {
  const patterns: RegExp[] = [];
  for (const secret of secretValues) {
    if (!secret) continue;
    patterns.push(new RegExp(escapeRegExp(secret), "g"));
    try {
      const b64 = Buffer.from(secret, "utf8").toString("base64");
      if (b64.length >= 4) patterns.push(new RegExp(escapeRegExp(b64), "g"));
    } catch {
      // ignore encoding failure, not fatal to redaction of other variants
    }
    const urlEncoded = encodeURIComponent(secret);
    if (urlEncoded !== secret) patterns.push(new RegExp(escapeRegExp(urlEncoded), "g"));
  }
  return patterns;
}

function redactWith(text: string, patterns: RegExp[]): string {
  let out = text;
  for (const pattern of patterns) {
    out = out.replace(pattern, REDACTED_PLACEHOLDER);
  }
  return out;
}

/**
 * Generic patterns are greedy and open-ended: a match that touches the END
 * of the buffer may be only the head of a longer token still arriving (a JWT
 * signature cut mid-way matched `{5,}` and got replaced — the rest of the
 * signature then streamed out in the clear). Mid-stream, leave such a match
 * untouched (it stays in the held-back tail); `flush()` redacts everything.
 */
function redactGenericWith(text: string, patterns: RegExp[], finalize: boolean): string {
  let out = text;
  for (const pattern of patterns) {
    const source = out;
    out = source.replace(pattern, (match: string, offset: number) =>
      !finalize && offset + match.length === source.length ? match : REDACTED_PLACEHOLDER,
    );
  }
  return out;
}

export interface SecretRedactorOptions {
  secretValues: string[];
}

export class SecretRedactor {
  private readonly knownPatterns: RegExp[];
  private readonly baseHoldBack: number;
  private tail = "";

  constructor(options: SecretRedactorOptions) {
    this.knownPatterns = buildKnownSecretPatterns(options.secretValues);
    const longestSecret = Math.max(0, ...options.secretValues.map((s) => s.length));
    this.baseHoldBack = Math.max(
      BASE_HOLD_BACK_CHARS,
      longestSecret * KNOWN_SECRET_HOLD_BACK_FACTOR,
    );
  }

  // Emits safe-to-flush redacted text; holds back a tail that might still be
  // part of a secret split across the chunk boundary.
  feed(chunk: string): string {
    const combined = this.tail + chunk;
    const redacted = redactGenericWith(
      redactWith(combined, this.knownPatterns),
      GENERIC_TOKEN_PATTERNS,
      false,
    );
    const holdBack = Math.max(this.baseHoldBack, trailingTokenRunLength(redacted));

    if (redacted.length <= holdBack) {
      this.tail = redacted;
      return "";
    }
    const splitAt = redacted.length - holdBack;
    const safe = redacted.slice(0, splitAt);
    this.tail = redacted.slice(splitAt);
    return safe;
  }

  // End of stream: redact + emit whatever tail remains.
  flush(): string {
    const redacted = redactGenericWith(
      redactWith(this.tail, this.knownPatterns),
      GENERIC_TOKEN_PATTERNS,
      true,
    );
    this.tail = "";
    return redacted;
  }
}
